import type { SavedJobState } from "../../../__generated__/types.gqlgenerated";

import { makeVar, type ReactiveVar, useReactiveVar } from "@apollo/client";

/**
 * Cross-provider facts about a job that need to update immediately in every RN root.
 *
 * Add fields here as provider-local state is migrated. For example, disliked-job state can
 * move here without changing consumers of savedJobState.
 */
export type JobInteractionState = Readonly<{
  savedJobState?: SavedJobState;
  isReported?: boolean;
}>;

const EMPTY_JOB_INTERACTION_STATE: JobInteractionState = Object.freeze({});

// Keep one reactive object per job. A single reactive var containing every job would notify
// every mounted JobCard when any job changes because Apollo reactive vars have no selectors.
// Grouping all facts for the same job still gives future fields (for example, disliked state)
// one shared extension point without causing cross-job rerenders.
const jobInteractionVars = new Map<string, ReactiveVar<JobInteractionState>>();
let currentAccountId: string | undefined;

function getJobInteractionVar(jobKey: string): ReactiveVar<JobInteractionState> {
  let jobInteractionVar = jobInteractionVars.get(jobKey);
  if (!jobInteractionVar) {
    jobInteractionVar = makeVar(EMPTY_JOB_INTERACTION_STATE);
    jobInteractionVars.set(jobKey, jobInteractionVar);
  }
  return jobInteractionVar;
}

export function useJobInteractionState(jobKey: string): JobInteractionState {
  return useReactiveVar(getJobInteractionVar(jobKey));
}

export function updateJobInteractionState(
  jobKey: string,
  update: Partial<JobInteractionState>,
): void {
  const jobInteractionVar = getJobInteractionVar(jobKey);
  jobInteractionVar({ ...jobInteractionVar(), ...update });
}

export function getSavedJobState(jobKey: string): SavedJobState | undefined {
  return getJobInteractionVar(jobKey)().savedJobState;
}

export function setSavedJobState(jobKey: string, savedJobState: SavedJobState | undefined): void {
  updateJobInteractionState(jobKey, { savedJobState });
}

export function useSavedJobState(
  jobKey: string,
  providerState: SavedJobState | undefined,
): SavedJobState | undefined {
  return useJobInteractionState(jobKey).savedJobState ?? providerState;
}

export function setJobReported(jobKey: string, isReported: boolean): void {
  updateJobInteractionState(jobKey, { isReported });
}

export function useIsJobReported(jobKey: string): boolean {
  return useJobInteractionState(jobKey).isReported === true;
}

/** Reset all mounted consumers without invalidating the reactive vars they subscribe to. */
export function resetJobInteractionsState(): void {
  for (const jobInteractionVar of jobInteractionVars.values()) {
    jobInteractionVar(EMPTY_JOB_INTERACTION_STATE);
  }
}

/** Reset user-specific facts when a different signed-in account mounts an RN provider. */
export function syncJobInteractionsAccount(accountId: string | null | undefined): void {
  // ProviderRoot wraps surfaces that do not all receive nativeContext. Missing context must not
  // clear another mounted provider's state; only an explicit null means signed out.
  if (accountId === undefined) return;
  if (accountId === null) {
    resetJobInteractionsForLogout();
    return;
  }
  if (currentAccountId !== undefined && currentAccountId !== accountId) {
    resetJobInteractionsState();
  }
  currentAccountId = accountId;
}

export function resetJobInteractionsForLogout(): void {
  resetJobInteractionsState();
  currentAccountId = undefined;
}
